/**
 * Markdown conversion utility for OfficeParserAST.
 *
 * Converts the unified AST content nodes into well-formatted Markdown text.
 * Handles all node types: headings, paragraphs, text (with formatting),
 * lists, tables, images, charts, notes, slides, sheets, and pages.
 *
 * @module markdownUtils
 */

import { OfficeContentNode, HeadingMetadata, ListMetadata, ImageMetadata, ChartMetadata, SlideMetadata, SheetMetadata, PageMetadata, NoteMetadata, TextMetadata, HeaderFooterMetadata } from '../types';

/**
 * Converts an array of OfficeContentNode into Markdown text.
 *
 * @param content - The content nodes from the AST
 * @param config - Configuration options
 * @param config.newlineDelimiter - Delimiter for newlines (default: '\n')
 * @returns Formatted Markdown string
 */
export function astToMarkdown(content: OfficeContentNode[], config: { newlineDelimiter?: string }): string {
    const nl = config.newlineDelimiter ?? '\n';
    const lines: string[] = [];

    for (const node of content) {
        const result = renderNode(node, nl);
        if (result !== '') {
            lines.push(result);
        }
    }

    return lines.join(nl + nl);
}

/**
 * Renders a single content node to Markdown.
 */
function renderNode(node: OfficeContentNode, nl: string): string {
    switch (node.type) {
        case 'heading':
            return renderHeading(node, nl);
        case 'paragraph':
            return renderParagraph(node, nl);
        case 'text':
            return renderText(node);
        case 'list':
            return renderList(node, nl);
        case 'table':
            return renderTable(node, nl);
        case 'image':
            return renderImage(node);
        case 'chart':
            return renderChart(node);
        case 'note':
            return renderNote(node, nl);
        case 'slide':
            return renderSlide(node, nl);
        case 'sheet':
            return renderSheet(node, nl);
        case 'page':
            return renderPage(node, nl);
        case 'header':
            return renderHeaderFooter(node, nl, 'Header');
        case 'footer':
            return renderHeaderFooter(node, nl, 'Footer');
        case 'row':
        case 'cell':
            // These are handled by their parent (table)
            return renderChildren(node, nl);
        default:
            return node.text || '';
    }
}

/**
 * Renders a heading node: `# Heading`, `## Heading`, etc.
 */
function renderHeading(node: OfficeContentNode, nl: string): string {
    const meta = node.metadata as HeadingMetadata | undefined;
    const level = Math.min(Math.max(meta?.level ?? 1, 1), 6);
    const prefix = '#'.repeat(level) + ' ';
    const text = node.children ? renderInlineChildren(node, nl) : (node.text || '');
    return prefix + text;
}

/**
 * Renders a paragraph node as plain text with a trailing newline.
 */
function renderParagraph(node: OfficeContentNode, nl: string): string {
    if (node.children && node.children.length > 0) {
        return renderInlineChildren(node, nl);
    }
    return node.text || '';
}

/**
 * Renders inline children (text runs within a paragraph/heading).
 * Joins them without separator since they are inline fragments.
 */
function renderInlineChildren(node: OfficeContentNode, nl: string): string {
    if (!node.children) return node.text || '';
    return node.children.map(child => {
        if (child.type === 'text') {
            return renderText(child);
        }
        // For nested containers (e.g., list items inside paragraphs), render recursively
        return renderNode(child, nl);
    }).join('');
}

/**
 * Renders a text node with inline formatting applied.
 */
function renderText(node: OfficeContentNode): string {
    let text = node.text || '';
    if (!text) return '';

    const fmt = node.formatting;
    const meta = node.metadata as TextMetadata | undefined;

    // Apply formatting wrappers (innermost first)
    if (fmt) {
        if (fmt.strikethrough) {
            text = `~~${text}~~`;
        }
        if (fmt.underline) {
            text = `<u>${text}</u>`;
        }
        if (fmt.bold && fmt.italic) {
            text = `***${text}***`;
        } else if (fmt.bold) {
            text = `**${text}**`;
        } else if (fmt.italic) {
            text = `*${text}*`;
        }
        if (fmt.subscript) {
            text = `<sub>${text}</sub>`;
        }
        if (fmt.superscript) {
            text = `<sup>${text}</sup>`;
        }
    }

    // Apply link if present
    if (meta?.link) {
        text = `[${text}](${meta.link})`;
    }

    return text;
}

/**
 * Renders a list item node with proper indentation and marker.
 */
function renderList(node: OfficeContentNode, nl: string): string {
    const meta = node.metadata as ListMetadata | undefined;
    const indent = '  '.repeat(meta?.indentation ?? 0);
    const text = node.children ? renderInlineChildren(node, nl) : (node.text || '');

    if (meta?.listType === 'ordered') {
        const index = (meta.itemIndex ?? 0) + 1;
        return `${indent}${index}. ${text}`;
    }
    return `${indent}- ${text}`;
}

/**
 * Renders a table as a Markdown table with header separator.
 * First row is treated as the header.
 */
function renderTable(node: OfficeContentNode, nl: string): string {
    if (!node.children || node.children.length === 0) return '';

    const rows = node.children.filter(r => r.type === 'row');
    if (rows.length === 0) return '';

    const tableRows: string[][] = [];
    let maxCols = 0;

    for (const row of rows) {
        const cells: string[] = [];
        if (row.children) {
            for (const cell of row.children) {
                cells.push(extractCellText(cell, nl));
            }
        }
        if (cells.length > maxCols) maxCols = cells.length;
        tableRows.push(cells);
    }

    // Pad rows to have equal columns
    for (const row of tableRows) {
        while (row.length < maxCols) {
            row.push('');
        }
    }

    if (tableRows.length === 0 || maxCols === 0) return '';

    const lines: string[] = [];

    // Header row
    lines.push('| ' + tableRows[0].map(c => c.replace(/\|/g, '\\|')).join(' | ') + ' |');

    // Separator
    lines.push('| ' + tableRows[0].map(() => '---').join(' | ') + ' |');

    // Data rows
    for (let i = 1; i < tableRows.length; i++) {
        lines.push('| ' + tableRows[i].map(c => c.replace(/\|/g, '\\|')).join(' | ') + ' |');
    }

    return lines.join(nl);
}

/**
 * Extracts plain text from a table cell by recursing into its children.
 */
function extractCellText(cell: OfficeContentNode, nl: string): string {
    if (cell.children && cell.children.length > 0) {
        return cell.children.map(child => {
            if (child.type === 'text') return renderText(child);
            if (child.children) return extractCellText(child, nl);
            return child.text || '';
        }).filter(t => t !== '').join(' ');
    }
    return cell.text || '';
}

/**
 * Renders an image node as `![altText](attachmentName)`.
 */
function renderImage(node: OfficeContentNode): string {
    const meta = node.metadata as ImageMetadata | undefined;
    const alt = meta?.altText || 'image';
    const src = meta?.attachmentName || '';
    return `![${alt}](${src})`;
}

/**
 * Renders a chart node as a text reference.
 */
function renderChart(node: OfficeContentNode): string {
    const meta = node.metadata as ChartMetadata | undefined;
    const name = meta?.attachmentName || 'chart';
    if (node.text) {
        return `[Chart: ${node.text}]`;
    }
    return `[Chart: ${name}]`;
}

/**
 * Renders a note (footnote/endnote) using Markdown footnote syntax.
 */
function renderNote(node: OfficeContentNode, nl: string): string {
    const meta = node.metadata as NoteMetadata | undefined;
    const id = meta?.noteId || '0';
    const text = node.children ? renderInlineChildren(node, nl) : (node.text || '');
    return `[^${id}]: ${text}`;
}

/**
 * Renders a slide node with a horizontal rule separator and slide heading.
 */
function renderSlide(node: OfficeContentNode, nl: string): string {
    const meta = node.metadata as SlideMetadata | undefined;
    const parts: string[] = [];

    parts.push('---');
    if (meta?.slideNumber) {
        parts.push(`### Slide ${meta.slideNumber}`);
    }

    if (node.children) {
        for (const child of node.children) {
            const rendered = renderNode(child, nl);
            if (rendered !== '') {
                parts.push(rendered);
            }
        }
    }

    return parts.join(nl + nl);
}

/**
 * Renders a sheet node with a heading for the sheet name.
 */
function renderSheet(node: OfficeContentNode, nl: string): string {
    const meta = node.metadata as SheetMetadata | undefined;
    const parts: string[] = [];

    if (meta?.sheetName) {
        parts.push(`## ${meta.sheetName}`);
    }

    if (node.children) {
        // If the sheet contains rows directly, wrap them as a table
        const rows = node.children.filter(c => c.type === 'row');
        if (rows.length > 0) {
            const tableNode: OfficeContentNode = { type: 'table', children: rows };
            const rendered = renderTable(tableNode, nl);
            if (rendered !== '') {
                parts.push(rendered);
            }
        }

        // Render non-row children normally
        for (const child of node.children) {
            if (child.type !== 'row') {
                const rendered = renderNode(child, nl);
                if (rendered !== '') {
                    parts.push(rendered);
                }
            }
        }
    }

    return parts.join(nl + nl);
}

/**
 * Renders a page node with a page separator comment.
 */
function renderPage(node: OfficeContentNode, nl: string): string {
    const meta = node.metadata as PageMetadata | undefined;
    const parts: string[] = [];

    if (meta?.pageNumber) {
        parts.push(`<!-- Page ${meta.pageNumber} -->`);
    }

    if (node.children) {
        for (const child of node.children) {
            const rendered = renderNode(child, nl);
            if (rendered !== '') {
                parts.push(rendered);
            }
        }
    } else if (node.text) {
        parts.push(node.text);
    }

    return parts.join(nl + nl);
}

/**
 * Renders a header or footer node as a blockquote with a label.
 */
function renderHeaderFooter(node: OfficeContentNode, nl: string, label: string): string {
    const parts: string[] = [];
    if (node.children) {
        for (const child of node.children) {
            const rendered = renderNode(child, nl);
            if (rendered !== '') {
                parts.push(`> **${label}:** ${rendered}`);
            }
        }
    } else if (node.text) {
        parts.push(`> **${label}:** ${node.text}`);
    }
    return parts.join(nl);
}

/**
 * Renders children of a generic container node.
 */
function renderChildren(node: OfficeContentNode, nl: string): string {
    if (node.children) {
        return node.children.map(child => renderNode(child, nl)).filter(t => t !== '').join(nl);
    }
    return node.text || '';
}
